const JUDGE0_URL = 'https://ce.judge0.com';

function judgeTests(problem) {
  return problem.testCases.map((test) => ({
    input: test.input,
    expected: test.expected,
  }));
}

function range(start, end, step = 1) {
  const actualStart = end === undefined ? 0 : start;
  const actualEnd = end === undefined ? start : end;
  const values = [];
  for (let value = actualStart; value < actualEnd; value += step) values.push(value);
  return values;
}

function repeatValue(value, count) {
  return Array.from({ length: count }, () => value);
}

function generatedHiddenTests(problem) {
  switch (problem.id) {
    case 'valid-parentheses':
      return [{ input: { s: '('.repeat(20000) + ')'.repeat(20000) }, expected: true }];
    case 'best-time-to-buy-and-sell-stock':
      return [{ input: { prices: range(1, 10001).reverse() }, expected: 0 }];
    case 'valid-palindrome':
      return [{ input: { s: `${'a'.repeat(30000)}:${'a'.repeat(30000)}` }, expected: true }];
    case 'valid-anagram':
      return [{ input: { s: `${'a'.repeat(20000)}${'b'.repeat(20000)}`, t: `${'b'.repeat(20000)}${'a'.repeat(20000)}` }, expected: true }];
    case 'binary-search':
      return [{ input: { nums: range(100000), target: 99999 }, expected: 99999 }];
    case 'contains-duplicate':
      return [{ input: { nums: range(12000).concat(11999) }, expected: true }];
    case 'maximum-subarray':
      return [{ input: { nums: Array.from({ length: 12000 }, () => 1) }, expected: 12000 }];
    case 'climbing-stairs':
      return [{ input: { n: 35 }, expected: 14930352 }];
    case 'merge-sorted-array':
      return [{ input: { nums1: range(0, 12000, 2), nums2: range(1, 12000, 2) }, expected: range(12000) }];
    case 'three-sum':
      return [{ input: { nums: range(1, 1801) }, expected: [] }];
    case 'product-of-array-except-self':
      return [{ input: { nums: Array.from({ length: 5000 }, () => 1) }, expected: Array.from({ length: 5000 }, () => 1) }];
    case 'top-k-frequent-elements':
      return [{ input: { nums: repeatValue(0, 5000).concat(repeatValue(1, 2500), repeatValue(2, 1000)), k: 2 }, expected: [0, 1] }];
    case 'subarray-sum-equals-k':
      return [{ input: { nums: Array.from({ length: 10000 }, () => 1), k: 2 }, expected: 9999 }];
    case 'longest-substring-without-repeating-characters':
      return [{ input: { s: 'abcdefghijklmnopqrstuvwxyz'.repeat(400) }, expected: 26 }];
    case 'minimum-size-subarray-sum':
      return [{ input: { target: 5000, nums: Array.from({ length: 10000 }, () => 1) }, expected: 5000 }];
    case 'rotate-array': {
      const nums = range(20000);
      return [{ input: { nums, k: 5000 }, expected: nums.slice(-5000).concat(nums.slice(0, -5000)) }];
    }
    case 'search-in-rotated-sorted-array':
      return [{ input: { nums: range(50000, 100000).concat(range(50000)), target: 49999 }, expected: 99999 }];
    case 'coin-change':
      return [{ input: { coins: [1, 7, 10], amount: 5000 }, expected: 500 }];
    case 'word-break':
      return [{ input: { s: `${'a'.repeat(1200)}b`, wordDict: ['a', 'aa', 'aaa', 'aaaa', 'aaaaa'] }, expected: false }];
    case 'sliding-window-maximum':
      return [{ input: { nums: range(12000), k: 6000 }, expected: range(5999, 12000) }];
    case 'trapping-rain-water':
      return [{ input: { height: [10000].concat(Array.from({ length: 8000 }, () => 0), [10000]) }, expected: 80000000 }];
    case 'median-of-two-sorted-arrays':
      return [{ input: { nums1: range(20000), nums2: range(20000, 40000) }, expected: 19999.5 }];
    default:
      return [];
  }
}

function buildPythonHiddenTests(problem) {
  if (problem.id !== 'two-sum') {
    const callArgs = problem.signature.args.map((arg) => `test["input"][${JSON.stringify(arg.name)}]`).join(', ');
    return `    hidden_tests = ${JSON.stringify(generatedHiddenTests(problem))}
    for test in hidden_tests:
        add_hidden("Hidden test", test["input"], test["expected"], solve(${callArgs}))`;
  }

  return `    hidden_nums = list(range(20000))
    hidden_nums[-2] = 100000000
    hidden_nums[-1] = 200000000
    hidden_expected = [19998, 19999]
    hidden_actual = solve(hidden_nums, 300000000)
    add_hidden("Hidden test", {"nums": "generated 20000 integers", "target": 300000000}, hidden_expected, hidden_actual)`;
}

function buildJavaScriptHiddenTests(problem) {
  if (problem.id !== 'two-sum') {
    const callArgs = problem.signature.args.map((arg) => `test.input[${JSON.stringify(arg.name)}]`).join(', ');
    return `  const hiddenTests = ${JSON.stringify(generatedHiddenTests(problem))};
  for (const test of hiddenTests) {
    addHidden("Hidden test", test.input, test.expected, solve(${callArgs}));
  }`;
  }

  return `  const hiddenNums = Array.from({ length: 20000 }, (_, index) => index);
  hiddenNums[19998] = 100000000;
  hiddenNums[19999] = 200000000;
  const hiddenExpected = [19998, 19999];
  const hiddenActual = solve(hiddenNums, 300000000);
  addHidden("Hidden test", { nums: "generated 20000 integers", target: 300000000 }, hiddenExpected, hiddenActual);`;
}

function buildPythonJudge0Source(problem, code) {
  const callArgs = problem.signature.args.map((arg) => `test["input"][${JSON.stringify(arg.name)}]`).join(', ');
  const hiddenTests = buildPythonHiddenTests(problem);

  return `
import json

user_code = ${JSON.stringify(code)}
tests = ${JSON.stringify(judgeTests(problem))}
namespace = {}

try:
    exec(user_code, namespace)
    solve = namespace.get("solve")
    if not callable(solve):
        raise Exception("Define a solve function with the problem arguments.")

    results = []
    for index, test in enumerate(tests):
        actual = solve(${callArgs})
        expected = test["expected"]
        results.append({
            "index": index,
            "name": f"Test {index + 1}",
            "passed": actual == expected,
            "input": test["input"],
            "expected": expected,
            "actual": actual
        })

    def add_hidden(name, input_value, expected, actual):
        results.append({
            "index": len(results),
            "name": name,
            "passed": actual == expected,
            "input": input_value,
            "expected": expected,
            "actual": actual
        })

${hiddenTests}

    print(json.dumps({
        "passed": all(item["passed"] for item in results),
        "results": results
    }))
except Exception as error:
    print(json.dumps({
        "passed": False,
        "error": str(error),
        "results": []
    }))
`;
}

function buildJavaScriptJudge0Source(problem, code) {
  const callArgs = problem.signature.args.map((arg) => `test.input[${JSON.stringify(arg.name)}]`).join(', ');
  const hiddenTests = buildJavaScriptHiddenTests(problem);

  return `
const userCode = ${JSON.stringify(code)};
const tests = ${JSON.stringify(judgeTests(problem))};
let solve;
try {
  const module = { exports: {} };
  const exports = module.exports;
  Function("module", "exports", userCode)(module, exports);
  solve = module.exports.solve || exports.solve || globalThis.solve;
  if (typeof solve !== "function") throw new Error("Export a solve function with the problem arguments.");
  const results = tests.map((test, index) => {
    const actual = solve(${callArgs});
    const passed = JSON.stringify(actual) === JSON.stringify(test.expected);
    return { index, name: \`Test \${index + 1}\`, passed, input: test.input, expected: test.expected, actual };
  });
  const addHidden = (name, input, expected, actual) => {
    results.push({
      index: results.length,
      name,
      passed: JSON.stringify(actual) === JSON.stringify(expected),
      input,
      expected,
      actual
    });
  };
${hiddenTests}
  console.log(JSON.stringify({ passed: results.every((item) => item.passed), results }));
} catch (error) {
  console.log(JSON.stringify({ passed: false, error: error.message, results: [] }));
}
`;
}

function buildJudge0Source(problem, languageKey, code) {
  if (languageKey === 'python') return buildPythonJudge0Source(problem, code);
  if (languageKey === 'javascript') return buildJavaScriptJudge0Source(problem, code);
  throw new Error('This TopSWE runner currently supports Python and JavaScript. C++ and Java wrappers can be ported next.');
}

export async function runJudge0Problem(problem, language, code) {
  const sourceCode = buildJudge0Source(problem, language.key, code);
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const endpoint = isLocalhost
    ? `${JUDGE0_URL}/submissions?base64_encoded=false&wait=true&fields=stdout,stderr,compile_output,message,status`
    : '/api/run-code';
  const body = isLocalhost
    ? {
        language_id: language.judge0Id,
        source_code: sourceCode,
        cpu_time_limit: 2,
        wall_time_limit: 5,
        memory_limit: 128000,
      }
    : {
        languageId: language.judge0Id,
        sourceCode,
      };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Judge0 request failed (${response.status}): ${text}`);
  }

  const submission = await response.json();
  if (submission.status?.id !== 3 && !submission.stdout) {
    throw new Error(submission.compile_output || submission.stderr || submission.message || submission.status?.description || 'Judge0 failed.');
  }

  try {
    return JSON.parse(String(submission.stdout || '').trim());
  } catch {
    throw new Error(submission.stderr || submission.compile_output || submission.message || 'Judge0 returned unreadable output.');
  }
}
